import {assertUnreachable} from '@wandb/weave/common/util/types';

import {createMultiRunDefaultSpec} from '../../components/Workspace/createMultiRunDefaultSpec';
import {type SemanticLegendSettingsWithCategoricalConfig} from '../../components/WorkspaceDrawer/Settings/types';
import {type WorkspaceTemplate} from '../../components/WorkspaceTemplate/workspaceTemplatesQueries';
import {type ThunkResult} from '../../types/redux';
import * as ViewActions from '../views/actions';
import * as ViewActionsInternal from '../views/actionsInternal';
import {deleteView} from '../views/api';
import {type GroupPageViewSpec} from '../views/groupPage/types';
import {type ProjectPageViewSpec} from '../views/projectPage/types';
import {type RunPageViewSpec} from '../views/runPage/types';
import {type SweepPageViewSpec} from '../views/sweepPage/types';
import * as ViewThunks from '../views/thunks';
import {type LoadableView, type ViewType} from '../views/types';
import {
  displayWorkspaceError,
  load,
  newWorkspaceViews,
  removeWorkspaceView,
} from './actions';
import {findLegacyOrNwView} from './find';
import {
  copyToAndLoadPersonalWorkspace,
  createNewViewWithSpecAndLoad,
  createNewWorkspaceAndLoad,
  EMPTY_WORKSPACE_DISPLAY_NAME,
  migrateAndLoadLegacyWorkspace,
  saveWorkspace,
} from './namedWorkspaceActionsInternal';
import {nwToast} from './namedWorkspaceToast';
import {determineLoadActionForUrl} from './namedWorkspaceUrls';
import {viewIsDefault, viewNameColumnForNamedWorkspace} from './names';
import {loadPerms, type LoadPermsResult} from './nwPermissions';
import * as Selectors from './selectors';
import {
  createSharedNamedWorkspaceId,
  getNamedWorkspaceId,
  type NamedWorkspaceId,
  type WorkspaceEditScope,
} from './types';
import {handleNwHistoryNavigation} from './urlNavigation';
import {type WorkspaceUrl} from './urlTypes';

export const PRIMARY_WORKSPACE_DISPLAY_NAME = 'Primary';

// The actions in this file are the actions to call when a user indicates they want
// to interact with workspaces. For example, they might be called as result of the user clicking a button

/*
 small helper function helpful during dev work - deletes a list of views.
 it's easy to call from loadNamedWorkpace - you'll need to use this snippet:
 return async (dispatch, getState, client) => {
    deleteViews(client, view_id_list_here)
*/
// eslint-disable-next-line prefer-const
export let delView = null;
export const deleteViews = (client: any, viewServerIds: string[]) =>
  viewServerIds.forEach(id => deleteView(client, id, false));

/**
 * Main entry point for switching views displayed in the user's workspace UI
 */
export const loadNamedWorkspace = (
  workspaceStateId: string,
  viewCid: string,
  nwId: NamedWorkspaceId | 'legacy'
): ThunkResult<void> => {
  return async (dispatch, getState, client) => {
    dispatch(ViewActionsInternal.deleteUndoRedoHistory());
    await dispatch(load(workspaceStateId, viewCid, nwId));
    handleNwHistoryNavigation('replace', nwId);
  };
};

/**
 * This determines which workspace user should land and updates redux.
 * It also replaces the url search param to make sure it matches with
 * the view that was loaded into redux.
 * Example: if a user visits a legacy workspace url and this function
 * determines that we need to migrateLegacy, we'll also want to update
 * url search from "?workspace=user-jofang" to "?nw=nwuserjofang".
 */
export const loadNamedWorkspaceForUrl = (
  workspaceStateId: string,
  workspaceUrl: WorkspaceUrl,
  legacyAndNwViews: LoadableView[],
  viewType: ViewType,
  permsPromise?: Promise<LoadPermsResult>
): ThunkResult<Promise<string | void>> => {
  return async (dispatch, getState, client) => {
    const state = getState();
    const viewer = state.viewer.viewer;
    const workspace = state.workspaces[workspaceStateId];
    const relatedId = workspace?.relatedId;
    // Callers that started permission loading early (see init.ts) pass the
    // in-flight promise; otherwise fall back to loading perms here.
    const nwPermInfo = await (permsPromise ??
      loadPerms(client, viewer, workspace.entityName, workspace.projectName));
    // Agent chat can link to a workspace created after this page opened. Fetch
    // it if it is missing from the original view list.
    let views = legacyAndNwViews;
    if (workspaceUrl.type === 'named' && !viewIsDefault(workspaceUrl.id)) {
      const nwId = getNamedWorkspaceId({...workspaceUrl, relatedId});
      const username =
        workspaceUrl.nwType === 'personal workspace'
          ? workspaceUrl.username
          : undefined;
      const {foundView} = findLegacyOrNwView(
        username,
        views,
        relatedId,
        viewType,
        nwId
      );
      if (foundView == null) {
        const startAction = ViewActions.loadMetadataListStarted({
          entityName: workspace.entityName,
          projectName: workspace.projectName,
          viewType,
          viewName: viewNameColumnForNamedWorkspace(nwId),
        });
        dispatch(startAction);
        const fetched = await dispatch(
          ViewThunks.loadMetadataList(startAction)
        );
        // The user may have left the page while the request was running.
        if (getState().workspaces[workspaceStateId] == null) {
          return;
        }
        const cids = fetched.map(view => view.cid);
        dispatch(newWorkspaceViews(workspaceStateId, cids));
        const stateViews = getState().views.views;
        views = views.concat(cids.map(cid => stateViews[cid]));
      }
    }
    const action = determineLoadActionForUrl(
      workspaceUrl,
      viewer,
      views,
      relatedId,
      viewType,
      nwPermInfo
    );

    switch (action.type) {
      case 'load':
        dispatch(
          loadNamedWorkspace(workspaceStateId, action.viewCid, action.nwId)
        );
        handleNwHistoryNavigation('replace', action.nwId);
        break;
      case 'createAndLoadNewView':
        dispatch(
          createNewWorkspaceAndLoad(
            workspaceStateId,
            action.nwId,
            action.displayName
          )
        );
        handleNwHistoryNavigation('replace', action.nwId);
        break;
      case 'error':
        // In the future we will show the error in the main UI if appropriate
        dispatch(
          displayWorkspaceError(workspaceStateId, {
            errorCode: action.errorCode,
            userTitle: action.userTitle ?? 'Error loading workspace',
            userMessage:
              action.userMessage ?? 'This workspace could not be loaded.',
          })
        );
        break;
      case 'migrateLegacy':
        dispatch(
          migrateAndLoadLegacyWorkspace(
            workspaceStateId,
            action.legacyViewServerId,
            action.newNwId,
            action.newDisplayName
          )
        );
        handleNwHistoryNavigation('replace', action.newNwId);
        break;
      default:
        assertUnreachable(action);
    }
  };
};

/**
 * Main action to call when the user wants copy a workspace's current state to their personal workspace
 */
export function copyToOwn(workspaceStateId: string): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch(copyToAndLoadPersonalWorkspace(workspaceStateId));
  };
}

/**
 * Main action to call when the user wants to copy a workspace's current state to a new saved view.
 * The edit scope determines whether the new view is owner- or team-editable.
 * This does not affect the workspace that is currently being viewed.
 */
export function saveAsNewWorkspace(
  workspaceStateId: string,
  editScope: WorkspaceEditScope,
  semanticLegendSettings: SemanticLegendSettingsWithCategoricalConfig
): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();
    const workspace = state.workspaces[workspaceStateId];
    const viewer = state.viewer.viewer;
    if (!viewer || !viewer.username) {
      throw new Error('must have viewer to create new workspace');
    }

    // Grab the  spec from redux
    const specSelResult = Selectors.makeWorkspaceSpecSelector(
      workspaceStateId,
      workspace.viewType
    )(state);
    const specResultData = Selectors.getDataChecked(specSelResult);
    if (specResultData.fullSpec == null) {
      throw new Error('saveAsNewWorkspace - error loading spec');
    }

    const nwId = createSharedNamedWorkspaceId(workspace.relatedId);
    await dispatch(
      createNewViewWithSpecAndLoad(
        workspaceStateId,
        nwId,
        specResultData.fullSpec,
        EMPTY_WORKSPACE_DISPLAY_NAME,
        editScope,
        semanticLegendSettings
      )
    );
  };
}

export function createNewSavedView(
  workspaceStateId: string,
  workspaceTemplate?: WorkspaceTemplate
): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();
    const workspace = state.workspaces[workspaceStateId];
    const viewer = state.viewer.viewer;
    if (!workspace?.defaultSpec || viewer == null) {
      throw new Error('must have a workspace and viewer to create a new view');
    }

    const nwId = createSharedNamedWorkspaceId(workspace.relatedId);
    const spec = createDefaultSpecWithPendingWorkspaceMode(
      workspace.viewType,
      workspace.defaultSpec,
      workspaceTemplate
    );
    await dispatch(
      createNewViewWithSpecAndLoad(
        workspaceStateId,
        nwId,
        spec,
        EMPTY_WORKSPACE_DISPLAY_NAME,
        'owner',
        {}
      )
    );
  };
}

/**
 * Main action to call when the user wants to undo all changes since they first started in a session.
 * This is possible because we don't auto-save shared workspaces. We mark them as modified instead.
 */
export function undoUnsavedChanges(
  workspaceStateId: string
): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const workspaceSelector =
      Selectors.makeWorkspaceReduxStateSelector(workspaceStateId)(state);
    const workspaceResult = Selectors.getDataChecked(workspaceSelector);

    const workspaceViewSelector =
      Selectors.makeWorkspaceViewSelector(workspaceStateId)(state);
    const workspaceViewResult = Selectors.getDataChecked(workspaceViewSelector);

    if (workspaceResult?.nwId == null) {
      throw new Error('Error undoing unsaved changes');
    }

    dispatch(
      load(workspaceStateId, workspaceViewResult.view.cid, workspaceResult.nwId)
    );
  };
}

/**
 * Main action to call when the user wants to reset to default view. This is like a factory reset.
 * This should be an undoable action.
 */
export function resetToDefaultSpec(
  workspaceStateId: string,
  workspaceTemplate?: WorkspaceTemplate
): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();
    const workspaceSelector =
      Selectors.makeWorkspaceReduxStateSelector(workspaceStateId)(state);
    const workspaceResult = Selectors.getDataChecked(workspaceSelector);

    const workspaceViewSelector =
      Selectors.makeWorkspaceViewSelector(workspaceStateId)(state);
    const workspaceViewResult = Selectors.getDataChecked(workspaceViewSelector);

    const workspaceSpecSelector = Selectors.makeWorkspaceSpecSelector(
      workspaceStateId,
      workspaceResult.viewType
    )(state);
    const workspaceSpecResult = Selectors.getDataChecked(workspaceSpecSelector);

    if (workspaceResult?.nwId == null || workspaceResult.defaultSpec == null) {
      throw new Error('Error reseting view to default spec');
    }

    const resetSpec = createDefaultSpecWithPendingWorkspaceMode(
      workspaceResult.viewType,
      workspaceResult.defaultSpec,
      workspaceTemplate
    );

    dispatch(
      ViewActionsInternal.undoableUpdateViewSpec(
        {id: workspaceViewResult.view.cid},
        {
          prevSpec: workspaceSpecResult.fullSpec,
          newSpec: resetSpec,
        }
      )
    );
  };
}

export function createDefaultSpecWithPendingWorkspaceMode(
  viewType: ViewType,
  defaultSpec: unknown,
  workspaceTemplate?: WorkspaceTemplate
): unknown {
  if (viewType === 'run-view') {
    const runSpec = defaultSpec as RunPageViewSpec;
    return {
      ...runSpec,
      workspaceSettings: {
        ...runSpec.workspaceSettings,
        shouldAutoGeneratePanels: 'pending',
      },
    };
  }

  if (
    viewType === 'group-view' ||
    viewType === 'project-view' ||
    viewType === 'sweep-view'
  ) {
    const multiRunSpec = (
      workspaceTemplate
        ? createMultiRunDefaultSpec(workspaceTemplate)
        : defaultSpec
    ) as GroupPageViewSpec | ProjectPageViewSpec | SweepPageViewSpec;
    return {
      ...multiRunSpec,
      section: {
        ...multiRunSpec.section,
        workspaceSettings: {
          ...multiRunSpec.section.workspaceSettings,
          shouldAutoGeneratePanels: 'pending',
        },
      },
    };
  }

  return defaultSpec;
}

/**
 * Main action to call when the user wants to delete their shared workspace.
 * Users cannot delete their personal workspaces at the moment.
 */
export const deleteWorkspaceViewAndLoad = (
  workspaceStateId: string,
  loadingInfo: {
    viewCid?: string;
    nwId?: NamedWorkspaceId;
  }
): ThunkResult<void> => {
  return async (dispatch, getState) => {
    const state = getState();
    const workspace = state.workspaces[workspaceStateId];
    if (
      workspace == null ||
      workspace.viewRef == null ||
      workspace.nwId == null
    ) {
      nwToast('Error deleting workspace.');
      throw new Error('invalid state - deleteWorkspaceViewAndLoad');
    }

    await dispatch(removeWorkspaceView(workspaceStateId, workspace.viewRef.id));
    await dispatch(
      ViewActionsInternal.updateViewName(
        workspace.viewRef,
        viewNameColumnForNamedWorkspace(workspace.nwId, 'deleted')
      )
    );

    const view = state.views.views[workspace.viewRef?.id];
    const displayName =
      view.displayName.length === 0 ? 'workspace' : `"${view.displayName}"`;
    nwToast(`Successfully deleted ${displayName}`);

    if (loadingInfo?.viewCid && loadingInfo?.nwId) {
      await dispatch(
        loadNamedWorkspace(
          workspaceStateId,
          loadingInfo?.viewCid,
          loadingInfo?.nwId
        )
      );
      // Do a replace instead of a push because we don't want to hit a deleted view
      // when user uses the back/forward button
      handleNwHistoryNavigation('replace', loadingInfo?.nwId);
    }
  };
};

/**
 * Main action to call when the user wants to save their changes.
 * `confirmedServerUpdatedAt` is the newer server version the user was warned
 * about and chose to overwrite; the save is guarded against it instead of the
 * version this tab loaded, so it doesn't raise a second conflict prompt.
 */
export const saveWorkspaceChanges = (
  workspaceStateId: string,
  confirmedServerUpdatedAt?: string
): ThunkResult<void> => {
  return async (dispatch, getState) => {
    const cid = getState().workspaces[workspaceStateId]?.viewRef?.id;
    if (cid != null && confirmedServerUpdatedAt != null) {
      dispatch(
        ViewActionsInternal.overwriteSaveConflict(cid, confirmedServerUpdatedAt)
      );
    }
    dispatch(saveWorkspace(workspaceStateId));
  };
};
